<template>
  <div class="dashboard-block" v-if="!booking.is_offer">
    <h2>Admin Options</h2>

    <div class="panel">
      <div class="panel-body">

        <div class="row" v-if="$requestFormConfig.show_commission && monetaryCurrency">
          <div class="col-sm-4">
            <h3>{{ clientName }} Commission</h3>
          </div>
          <div class="col-sm-8">
            <div :class="hasErrorClass($v.stateCommissionRate, 'form-group')">

              <div class="row">
                <div class="col-sm-5 col-md-5">
                  <div class="input-group">
                    <input
                      name="commission_rate"
                      type="number"
                      min="0"
                      autocomplete="off"
                      class="form-control"
                      v-model="$v.stateCommissionRate.$model"
                      :disabled="!!stateCommissionDollar"/>

                    <span class="input-group-addon"><i class="fa fa-percent"></i></span>
                  </div>

                  <vuelidate-help :field="$v.stateCommissionRate" :label="'Commission rate'" />
                </div>
                <div class="col-sm-1 col-md-1" style="padding: 0; padding-top: 10px; text-align: center;">OR</div>
                <div class="col-sm-5 col-md-5">
                  <div :class="hasErrorClass($v.stateCommissionDollar, 'form-group')">
                    <div class="input-group">
                      <span class="input-group-addon"><i class="fa fa-usd"></i></span>

                      <input
                        name="commission_dollar"
                        type="text"
                        class="form-control"
                        v-model="$v.stateCommissionDollar.$model"
                        :disabled="!!stateCommissionRate"
                      />
                    </div>

                    <vuelidate-help :field="$v.stateCommissionDollar" :label="'Commission dollar'" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="row" v-if="!$vlastMode">
          <div class="col-sm-6">
            <div class="form-group">
              <label class="control-label">{{ $starLabel | sentenceCase }} Expiration</label><br/>
              {{ $starLabelPlural | sentenceCase }} Can Expire:&nbsp;&nbsp;
              <toggle-button
                :sync="true"
                v-model="booking.talent_expire"
                :labels="{ checked: 'Yes', unchecked: 'No' }"
                :color="{ checked: $brandPrimary, unchecked: '#b7b7b7' }"/>
            </div>
          </div>
          <div class="col-sm-6">
            <div class="form-group">
              <label class="control-label">{{ $starLabel | sentenceCase }} Commenting</label><br/>
              {{ $starLabelPlural | sentenceCase }} Can Comment:&nbsp;&nbsp;
              <toggle-button
                :sync="true"
                v-model="booking.talent_can_comment"
                :labels="{ checked: 'Yes', unchecked: 'No' }"
                :color="{ checked: $brandPrimary, unchecked: '#b7b7b7' }"/>
            </div>
          </div>
          <div class="col-sm-6">
            <div class="form-group">
              <label class="control-label">Uploading Files</label><br/>
              Client can upload files:&nbsp;&nbsp;
              <toggle-button
                :sync="true"
                v-model="booking.can_upload_files"
                :labels="{ checked: 'Yes', unchecked: 'No' }"
                :color="{ checked: $brandPrimary, unchecked: '#b7b7b7' }"/>
            </div>
          </div>
        </div>
        <div class="row" v-if="!$vlastMode">
          <div class="col-sm-3">
            <div class="form-group">
              <label class="control-label">Request Expiration</label><br/>
              Request Can Expire:&nbsp;&nbsp;
              <toggle-button
                :sync="true"
                v-model="booking.expires"
                :labels="{ checked: 'Yes', unchecked: 'No' }"
                :color="{ checked: $brandPrimary, unchecked: '#b7b7b7' }"/>
            </div>
          </div>
          <div class="col-sm-3" v-if="booking.expires">
            <div class="form-group">
              <label></label>
              <flat-pickr
                v-model="booking.billboard_expiry_date"
                :config="flatPickerDateConfig"
                class="form-control pickdate"
                placeholder="Please Select"
                name="date"/>
            </div>
          </div>
        </div>

        <div v-if="$userIsGlobalAdmin && regionOptions.length > 1" :class="hasErrorClass($v.stateRegion, 'form-group')">
          <h3>Region</h3>
          <selectize
            class="selectize selectize-single"
            name="timezone"
            v-model="$v.stateRegion.$model"
            :options="regionOptions">
          </selectize>
          <vuelidate-help :field="$v.stateRegion" :label="'Region'" />
        </div>

        <div class="row">
          <div class="col-sm-6 col-md-6">
            <div class="form-group">
              <ladda-button
                class="btn m-t-label btn-success"
                :click="handleSaveCommissionRateCallback">
                Save Changes
              </ladda-button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { BookingStateMapper as stateMapper } from 'pickstar-common'
import { mapActions } from 'vuex'
import Selectize from '../../ext/codium/CodiumSelectize'
import moment from 'moment'
import FlatPickr from 'vue-flatpickr-component'
import { between, decimal, minValue, requiredIf } from 'vuelidate/lib/validators'
import VuelidateHelp from '../../misc/VuelidateHelp'
import { notify } from 'utils'

export default {
  mixins: [stateMapper],

  components: {
    Selectize,
    FlatPickr,
    VuelidateHelp
  },

  data () {
    return {
      regionOptions: []
    }
  },

  validations: {
    stateCommissionRate: {
      decimal,
      between: between(1, 100)
    },
    stateCommissionDollar: {
      decimal,
      minValue: minValue(0)
    },
    stateRegion: {
      required: requiredIf(function () {
        return this.$userIsGlobalAdmin && this.regionOptions.length > 1
      })
    }
  },

  props: {
    booking: {
      required: true,
      type: Object
    },
    propertyMutator: {
      required: true,
      type: Function
    }
  },

  mounted () {
    this.getRegions().then(response => {
      this.regionOptions = response.data.map(item => {
        return {
          id: item.id,
          text: item.name
        }
      })
    })
  },

  computed: {
    flatPickerDateConfig () {
      return {
        altInput: true,
        altFormat: 'd/m/Y',
        dateFormat: 'Y-m-d',
        wrap: true,
        disable: [
          (date) => {
            if (this.$userIsAdmin) {
              return false
            }

            return moment(date).isBefore(moment().startOf('day'))
          }
        ]
      }
    },

    stateRegion: {
      get () {
        return this.booking.region_id
      },
      set (value) {
        this.propertyMutator({ property: 'region_id', value })
      }
    }
  },

  methods: {
    ...mapActions('booking', ['updateBookingAdminOptions']),
    ...mapActions('region', ['getRegions']),

    hasErrorClass (field, classes) {
      return field.$error ? `${classes} has-error` : `${classes}`
    },

    handleSaveCommissionRateCallback () {
      if (this.$v.$invalid) {
        this.$v.$touch()
        notify.error(`Some fields failed validation. Please fix them and try again`)
        return Promise.reject(new Error('Data invalid'))
      }
      return this.updateBookingAdminOptions({
        id: this.booking.id,
        commissionRate: this.booking.commission_rate,
        commissionDollar: this.stateCommissionDollar,
        commissionType: this.stateCommissionDollar ? 'dollar' : 'rate',
        talentExpire: this.booking.talent_expire,
        talentCanComment: this.booking.talent_can_comment,
        bookingExpires: this.booking.expires,
        expiryDate: this.booking.billboard_expiry_date,
        canUploadFiles: this.booking.can_upload_files,
        regionId: this.booking.region_id
      })
    }
  }
}
</script>
